/* eslint-disable lines-between-class-members */
import { Observable, Operation } from '@apollo/client';
import { Client, Message, StompSubscription } from '@stomp/stompjs';
import { ExecutionResult } from 'graphql';
import PubSub from 'pubsub-js';

import { WebSocketState } from '../../graphql/components';
import { recordBy } from '../../utils/array';
import { AuthorizationHeadersType } from '../AuthorizationProvider';
import { WEBSOCKET_DEFAULTS, webSocketStateOperationName } from './constants';
import { CustomSubscriptionClientOptions, SubscriptionDefinition } from './types';

interface WSSubscriptions {
  [key: string]: {
    // operation name to count of subscribers and websocket subscription
    count: number;
    request: Operation;
    subscription?: StompSubscription; // in case of client subscriptions, it is undefined
  };
}

interface UnhandledWSSubscriptions {
  [key: string]: {
    // dynamic channel name
    count: number;
    request: Operation;
  };
}

interface SubscriptionOperationContext {
  dynamicChannelName: string;
  request: Operation;
  definition: AnySubscriptionDefinition;
}

interface DataMessageType {
  type: 'data';
  data: any; // whatever comes from BE
  [key: string]: any;
}

interface ErrorMessageType {
  type: 'error';
  error: any;
}

interface CompleteMessageType {
  type: 'complete';
}

type PublishMessageType = DataMessageType | ErrorMessageType | CompleteMessageType;
type AnySubscriptionDefinition = SubscriptionDefinition<any, any>;

export class CustomSubscriptionClient {
  private readonly url;
  private stompClient: Client;

  private readonly CONTEXT_NAME = 'subscriptionOperationContext';
  private readonly isDebugEnabled: boolean;
  private readonly subscriptionDefinitionsRecord: Record<string, AnySubscriptionDefinition>;
  private readonly webSocketStateDefinition: AnySubscriptionDefinition;

  private wsSubscriptions: WSSubscriptions = {};
  private unhandledSubscriptions: UnhandledWSSubscriptions = {};
  private hasError = false;

  private readonly getHeaders: () => AuthorizationHeadersType;
  private readonly isPermittedToOpenWS: () => boolean;

  constructor(
    url: string,
    isDebugEnabled: boolean,
    subscriptionDefinitions: AnySubscriptionDefinition[],
    getHeaders: () => AuthorizationHeadersType,
    isPermittedToOpenWS: () => boolean,
    options?: CustomSubscriptionClientOptions
  ) {
    this.url = url;
    this.isDebugEnabled = isDebugEnabled;
    this.subscriptionDefinitionsRecord = recordBy(subscriptionDefinitions, 'operationName');

    const webSocketStateDefinition = subscriptionDefinitions.find(
      (definition) => definition.operationName === webSocketStateOperationName
    );
    if (!webSocketStateDefinition) {
      throw Error('[CustomSubscriptionClient]: Cannot be instantiated without web socket state definition!');
    }
    this.webSocketStateDefinition = webSocketStateDefinition;
    this.getHeaders = getHeaders;
    this.isPermittedToOpenWS = isPermittedToOpenWS;

    this.stompClient = new Client({
      // new SockJS(url) -- use `SockJS` when there are issues with connections ("sockjs-client": "^1.6.1")
      webSocketFactory: () => new WebSocket(this.url),

      beforeConnect: () => {
        this.stompClient.connectHeaders = {
          ...this.stompClient.connectHeaders,
          ...this.getHeaders(),
        };
      },

      // callbacks
      onConnect: () => {
        this.reconnectPreviousSubscriptions();
        this.flushUnhandledSubscriptions();
        options?.onConnected?.();
      },

      onStompError: (iFrame) => this.handleWebSocketState('stompError', undefined, 'Stomp error.', iFrame),
      onWebSocketClose: (closeEvent) => this.handleWebSocketState('close', undefined, 'WebSocket closed.', closeEvent),
      onWebSocketError: (event) => this.handleWebSocketState('error', undefined, 'WebSocket error.', event),
      onDisconnect: (iFrame) => this.handleWebSocketState('disconnect', undefined, 'WebSocket disconnected.', iFrame),

      reconnectDelay: options?.reconnectDelay ?? WEBSOCKET_DEFAULTS.reconnectDelay,
      heartbeatIncoming: options?.incomingHeartBeat ?? WEBSOCKET_DEFAULTS.heartbeatIncoming,
      heartbeatOutgoing: options?.outgoingHeartBeat ?? WEBSOCKET_DEFAULTS.heartbeatIncoming,
    });

    if (this.isDebugEnabled) {
      this.stompClient.debug = (msg) => this.debug('log', msg);
    }

    // do not open ws connection when user is not permitted to
    if (!this.isPermittedToOpenWS()) {
      return;
    }

    this.connect();
  }

  // logging of incoming messages is handled internally by @stomp
  private handleMessage(request: Operation, message: Message) {
    const {
      dynamicChannelName,
      definition: { transformFn },
    } = this.getRequestContext(request);

    let data;
    let errorMessage;

    try {
      data = JSON.parse(message.body);
      errorMessage = undefined;
    } catch (_) {
      data = undefined;
      errorMessage = 'Failed to serialize message body!';
      this.debug('error', errorMessage);
    }

    if (!data && errorMessage) {
      this.handleWebSocketState('stompError', dynamicChannelName, errorMessage);
      return;
    }

    PubSub.publish(dynamicChannelName, {
      type: 'data',
      data: {
        ...transformFn(data),
      },
      error: undefined,
    } as DataMessageType);

    this.clearPreviousError();
  }

  private clearPreviousError() {
    if (!this.hasError) {
      // prevent publish of unnecessary events
      return;
    }

    this.handleWebSocketState('active', undefined, '');
    this.hasError = false;
  }

  private handleWebSocketState(
    state: 'error' | 'disconnect' | 'close' | 'stompError' | 'active',
    sourceChannel?: string,
    description?: string,
    data?: any
  ) {
    const { channel } = this.webSocketStateDefinition;
    this.debug(
      'error',
      `Received error message: "${description}" from channel with name: "${sourceChannel ?? channel}"`
    );

    /**
     * It is possible to propagate following properties in order to determine exact state:
     *    this.stompClient.webSocket.readyState
     *    this.stompClient.state
     */
    PubSub.publish(this.webSocketStateDefinition.channel, {
      data: {
        webSocketState: {
          __typename: 'WebSocketState',
          state,
          error: {
            __typename: 'WebSocketError',
            message: description,
            data,
          },
        } as WebSocketState,
      },
    });

    this.hasError = true;
  }

  private checkSubscription(request: Operation) {
    const {
      dynamicChannelName,
      definition: { operationName },
    } = this.getRequestContext(request);
    const storedSubscription = this.wsSubscriptions[dynamicChannelName];

    if (storedSubscription) {
      storedSubscription.count += 1;
      this.debug(
        'debug',
        `Subscription for operation with name: "${operationName}" and channel: "${dynamicChannelName}" has new subscriber. New subscriber count is ${storedSubscription.count}.`
      );
      return;
    }

    // if subscription occurs before client is connected to websocket, store it and flush on connected
    if (!this.stompClient.connected) {
      this.debug(
        'debug',
        `Subscription request for operation with name: "${operationName}" and channel: "${dynamicChannelName}" before WS connection was established.`
      );

      if (this.unhandledSubscriptions[dynamicChannelName]) {
        this.unhandledSubscriptions[dynamicChannelName].count += 1;
        return;
      }

      this.unhandledSubscriptions[dynamicChannelName] = {
        count: 1,
        request,
      };

      this.connect();
      return;
    }

    this.subscribeInternal(request);
    this.debug(
      'debug',
      `Subscription for operation with name: "${operationName}" was created to channel with name: "${dynamicChannelName}".`
    );
  }

  public request(request: Operation): Observable<ExecutionResult> {
    const { operationName, variables } = request;
    const subscriptionDefinition = this.subscriptionDefinitionsRecord[operationName];

    if (!subscriptionDefinition) {
      this.debug('warn', `Received subscription request for unknown operation with name: "${operationName}".`);
      return Observable.from([]);
    }

    const dynamicChannelName = subscriptionDefinition.createDynamicChannelName(variables);

    // set context for future use
    this.setRequestContext(request, subscriptionDefinition, dynamicChannelName);
    this.checkSubscription(request);

    return new Observable((subscriber) => {
      const subscriptionToken = PubSub.subscribe(dynamicChannelName, (message, data: PublishMessageType) => {
        if (data.type === 'error') {
          subscriber.error(data.error[0]);
          return;
        }

        if (data.type === 'complete') {
          subscriber.complete();
          return;
        }

        subscriber.next(data);
      });

      return () => this.unSubscribe(subscriptionToken, operationName, dynamicChannelName);
    });
  }

  private unSubscribe(subscriptionToken: PubSubJS.Token, operationName: string, channelName: string) {
    PubSub.unsubscribe(subscriptionToken);
    const storedSubscription = this.wsSubscriptions[channelName];

    if (!storedSubscription) {
      this.debug(
        'warn',
        `Invalid unsubscribe occurred, check operation with name: "${operationName}" and channel: "${channelName}."`
      );
      return;
    }

    // decrease number of subscribers
    storedSubscription.count -= 1;
    this.debug(
      'debug',
      `Unsubscribed from operation with name: "${operationName}" and channel: "${channelName}". New subscriber count is ${storedSubscription.count}.`
    );

    // if number of subscribers are 0, unsubscribe from WS channel to minimize network traffic
    if (storedSubscription.count < 1) {
      this.debug(
        'debug',
        `Subscription for operation with name: "${operationName}" and channel: "${channelName}" was closed, due to 0 subscribers.`
      );
      storedSubscription.subscription?.unsubscribe();
      delete this.wsSubscriptions[channelName];
    }
  }

  private flushUnhandledSubscriptions() {
    Object.keys(this.unhandledSubscriptions).forEach((key) => {
      const { count, request } = this.unhandledSubscriptions[key];

      this.debug('debug', `Flushing unhandled subscription for channel: "${key}" with ${count} subscribers.`);

      this.subscribeInternal(request, count);
      delete this.unhandledSubscriptions[key];
    });
  }

  // TODO 22. 8. 2022 (david.salamon) ->  Fired after connection was dropped and then reconnected.
  //                                      Most probably when session persistence is implemented on BE, this method
  //                                      should be removed.
  private reconnectPreviousSubscriptions() {
    this.debug('log', 'Reconnecting previous subscriptions...');

    Object.keys(this.wsSubscriptions).forEach((key) => {
      const { request, subscription, count } = this.wsSubscriptions[key];
      subscription?.unsubscribe();
      this.subscribeInternal(request, count);
    });
  }

  private subscribeInternal(request: Operation, count?: number) {
    const {
      dynamicChannelName,
      definition: { isClientOnly },
    } = this.getRequestContext(request);

    this.wsSubscriptions[dynamicChannelName] = {
      count: count ?? 1,
      request,
      subscription: isClientOnly
        ? undefined
        : this.stompClient.subscribe(dynamicChannelName, (message) => this.handleMessage(request, message), {
            ...this.getHeaders(),
          }),
    };
  }

  private setRequestContext(request: Operation, definition: AnySubscriptionDefinition, dynamicChannelName: string) {
    request.setContext({
      [this.CONTEXT_NAME]: { request, definition, dynamicChannelName } as SubscriptionOperationContext,
    });
  }

  // TODO 3. 11. 2022 (david.salamon) -> Maybe context validation should be implemented?
  private getRequestContext(request: Operation): SubscriptionOperationContext {
    return request.getContext()[this.CONTEXT_NAME] as SubscriptionOperationContext;
  }

  private debug(level: 'error' | 'debug' | 'log' | 'warn', message: any) {
    if (!this.isDebugEnabled) {
      return;
    }

    console[level]('[CustomSubscriptionClient]: ', message);
  }

  public getUrl() {
    return this.url;
  }

  public getStompClient() {
    return this.stompClient;
  }

  public connect() {
    return this.stompClient.activate();
  }

  public destroy() {
    this.debug('log', 'Destroying...');

    // clear PubSub subscribers
    PubSub.clearAllSubscriptions();

    // clear local state
    Object.keys(this.wsSubscriptions).forEach((key) => {
      this.wsSubscriptions[key].subscription?.unsubscribe();
      delete this.wsSubscriptions[key];
    });
    Object.keys(this.unhandledSubscriptions).forEach((key) => delete this.unhandledSubscriptions[key]);

    // deactivate stomp
    this.stompClient.deactivate();
    this.stompClient.forceDisconnect();
  }
}
